/**
 * Full-Page Agent Details View for Admin
 * Shows agent profile, students, and applications with filters
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { agentsAPI, applicationsAPI, agentBankAPI, agentAgreementAPI, openAuthenticatedFile } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/lib/auth';
import AgentAgreement from '@/components/agent/AgentAgreement';
import { 
  ArrowLeft, Building2, Users, FileText, Mail, Phone, 
  GraduationCap, MapPin, Calendar, Loader2, Pencil, 
  CheckCircle, XCircle, Filter, Globe, TrendingUp,
  Search, ExternalLink, Clock, CreditCard, Download, Eye, AlertCircle
} from 'lucide-react';

const AgentFullPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [agentDetails, setAgentDetails] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [savingAgent, setSavingAgent] = useState(false);
  
  // Bank details state
  const [bankDetails, setBankDetails] = useState(null);
  const [bankStatus, setBankStatus] = useState('not_submitted');
  const [agentDocuments, setAgentDocuments] = useState([]);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processingBank, setProcessingBank] = useState(false);
  
  // Agreement state
  const [agreementData, setAgreementData] = useState(null);
  
  // Compliance status
  const [complianceStatus, setComplianceStatus] = useState(null);
  
  // POC Assignment state
  const [showPOCDialog, setShowPOCDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [assigningPOC, setAssigningPOC] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [universityFilter, setUniversityFilter] = useState('all');
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (agentId) {
      fetchAgentDetails();
      fetchBankAndDocs();
      fetchAgreement();
      fetchComplianceStatus();
      fetchEmployees();
    }
  }, [agentId]);

  const fetchComplianceStatus = async () => {
    try {
      const res = await agentsAPI.getComplianceStatus(agentId);
      setComplianceStatus(res.data);
    } catch (error) {
      console.error('Error fetching compliance status:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleAssignPOC = async (pocId) => {
    setAssigningPOC(true);
    try {
      await agentsAPI.assignPOC(agentId, pocId);
      toast.success('POC assigned successfully');
      setShowPOCDialog(false);
      fetchAgentDetails();
      fetchComplianceStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign POC');
    } finally {
      setAssigningPOC(false);
    }
  };

  const fetchAgreement = async () => {
    try {
      const res = await agentAgreementAPI.adminGet(agentId);
      setAgreementData(res.data);
    } catch (error) {
      console.error('Error fetching agreement:', error);
    }
  };

  const handleSaveAgreement = async (data) => {
    await agentAgreementAPI.adminSave(agentId, data);
    fetchAgreement();
    fetchComplianceStatus();
  };

  const fetchBankAndDocs = async () => {
    try {
      const [bankRes, docsRes] = await Promise.all([
        agentBankAPI.adminGetBankDetails(agentId),
        agentBankAPI.adminGetDocuments(agentId)
      ]);
      setBankDetails(bankRes.data.bank_details);
      setBankStatus(bankRes.data.bank_status || 'not_submitted');
      setAgentDocuments(docsRes.data.documents || []);
    } catch (error) {
      console.error('Error fetching bank details:', error);
    }
  };

  const handleBankApproval = async (action) => {
    if (action === 'reject' && !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessingBank(true);
    try {
      await agentBankAPI.adminApproveBankDetails(agentId, action, action === 'reject' ? rejectReason : null);
      toast.success(`Bank details ${action}d successfully`);
      setBankStatus(action === 'approve' ? 'approved' : 'rejected');
      setShowRejectDialog(false);
      setRejectReason('');
    } catch (error) {
      toast.error('Failed to process bank approval');
    } finally {
      setProcessingBank(false);
    }
  };

  const fetchAgentDetails = async () => {
    setLoading(true);
    try {
      const response = await agentsAPI.getDetails(agentId);
      setAgentDetails(response.data);
    } catch (error) {
      toast.error('Failed to load agent details');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  // Get unique filter options
  const filterOptions = useMemo(() => {
    if (!agentDetails?.applications) return { countries: [], universities: [], intakes: [] };
    
    const countries = [...new Set(agentDetails.applications.map(a => a.country).filter(Boolean))].sort();
    const universities = [...new Set(agentDetails.applications.map(a => a.university_name).filter(Boolean))].sort();
    const intakes = [...new Set(agentDetails.applications.map(a => a.intake_date).filter(Boolean))].sort();
    
    return { countries, universities, intakes };
  }, [agentDetails]);

  // Filtered applications
  const filteredApplications = useMemo(() => {
    if (!agentDetails?.applications) return [];
    
    return agentDetails.applications.filter(app => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!app.student_name?.toLowerCase().includes(term) &&
            !app.course_name?.toLowerCase().includes(term) &&
            !app.university_name?.toLowerCase().includes(term)) {
          return false;
        }
      }
      if (countryFilter !== 'all' && app.country !== countryFilter) return false;
      if (universityFilter !== 'all' && app.university_name !== universityFilter) return false;
      if (intakeFilter !== 'all' && app.intake_date !== intakeFilter) return false;
      if (statusFilter !== 'all' && app.status !== statusFilter) return false;
      return true;
    });
  }, [agentDetails, searchTerm, countryFilter, universityFilter, intakeFilter, statusFilter]);

  // Filtered students
  const filteredStudents = useMemo(() => {
    if (!agentDetails?.students) return [];
    
    return agentDetails.students.filter(student => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!student.full_name?.toLowerCase().includes(term) &&
            !student.email?.toLowerCase().includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [agentDetails, searchTerm]);

  const clearFilters = () => {
    setSearchTerm('');
    setCountryFilter('all');
    setUniversityFilter('all');
    setIntakeFilter('all');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchTerm || countryFilter !== 'all' || universityFilter !== 'all' || intakeFilter !== 'all' || statusFilter !== 'all';

  const openEditDialog = () => {
    setEditingAgent({
      ...agentDetails.agent,
      phone: agentDetails.user?.phone || '',
    });
    setShowEditDialog(true);
  };

  const handleSaveAgent = async () => {
    if (!editingAgent) return;
    setSavingAgent(true);
    try {
      await agentsAPI.updateProfile(agentId, {
        full_name: editingAgent.full_name,
        phone: editingAgent.phone,
        company_name: editingAgent.company_name,
        country: editingAgent.country,
        city: editingAgent.city,
        years_experience: parseInt(editingAgent.years_experience) || 0,
        students_placed: parseInt(editingAgent.students_placed) || 0,
        british_council_certified: editingAgent.british_council_certified
      });
      toast.success('Agent profile updated!');
      setShowEditDialog(false);
      fetchAgentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setSavingAgent(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      enrolled: 'bg-blue-100 text-blue-700',
      submitted: 'bg-purple-100 text-purple-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <Navbar />
        <div className="pt-20 flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  if (!agentDetails) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <Navbar />
        <div className="pt-20 text-center">
          <p className="text-gray-500">Agent not found</p>
          <Button onClick={() => navigate('/admin')} className="mt-4">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Navbar />
      
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-full mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin')}
              className="border-gray-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Agent Management</h1>
              <p className="text-gray-500 dark:text-gray-400">View and manage agent profile, students, and applications</p>
            </div>
          </div>

          {/* Agent Profile Card */}
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                      {agentDetails.agent?.full_name}
                    </h2>
                    <Badge className={getStatusBadge(agentDetails.agent?.approval_status)}>
                      {agentDetails.agent?.approval_status}
                    </Badge>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">{agentDetails.agent?.company_name || 'Independent Agent'}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {agentDetails.user?.email}
                    </span>
                    {agentDetails.user?.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {agentDetails.user?.phone}
                      </span>
                    )}
                    {agentDetails.agent?.country && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {agentDetails.agent?.city && `${agentDetails.agent.city}, `}{agentDetails.agent?.country}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{agentDetails.stats?.total_students || 0}</p>
                  <p className="text-xs text-gray-500">Students</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{agentDetails.stats?.total_applications || 0}</p>
                  <p className="text-xs text-gray-500">Applications</p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{agentDetails.stats?.enrolled || 0}</p>
                  <p className="text-xs text-gray-500">Enrolled</p>
                </div>
                <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">{agentDetails.stats?.conversion_rate || 0}%</p>
                  <p className="text-xs text-gray-500">Conversion</p>
                </div>
              </div>
              
              {/* Edit Button */}
              {user?.is_super_admin && (
                <Button onClick={openEditDialog} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              )}
            </div>
          </div>

          {/* POC Assignment & Approval Status Card */}
          {(user?.is_super_admin || user?.permissions?.can_manage_agents) && (
            <div className="bg-white dark:bg-slate-800 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* POC Section */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                    <Users className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-white">Point of Contact (POC)</h3>
                    {agentDetails?.agent?.assigned_poc_name ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {agentDetails.agent.assigned_poc_name}
                        {agentDetails.agent.assigned_poc_email && (
                          <span className="text-gray-400"> ({agentDetails.agent.assigned_poc_email})</span>
                        )}
                      </p>
                    ) : (
                      <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> Not Assigned (Required for approval)
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowPOCDialog(true)}
                    className="ml-4"
                    data-testid="assign-poc-btn"
                  >
                    {agentDetails?.agent?.assigned_poc_name ? 'Change POC' : 'Assign POC'}
                  </Button>
                </div>

                {/* Compliance Summary */}
                <div className="flex items-center gap-4">
                  {complianceStatus && (
                    <div className="flex items-center gap-3">
                      <div className={`px-3 py-2 rounded-lg ${
                        complianceStatus.all_mandatory_complete 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : 'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {complianceStatus.all_mandatory_complete ? (
                          <span className="text-sm text-green-700 dark:text-green-400 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> All Required Docs
                          </span>
                        ) : (
                          <span className="text-sm text-red-700 dark:text-red-400 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" /> Missing Documents
                          </span>
                        )}
                      </div>
                      
                      {/* Can Approve Status */}
                      <Badge className={
                        complianceStatus.can_approve 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }>
                        {complianceStatus.can_approve ? 'Ready for Approval' : 'Cannot Approve Yet'}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bank Details & Documents Section */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Bank Account Details Card */}
            <Card className="border-2 border-teal-200 dark:border-teal-800">
              <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-teal-600" />
                    Bank Account Details
                  </CardTitle>
                  <Badge className={
                    bankStatus === 'approved' ? 'bg-green-100 text-green-700' :
                    bankStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                    bankStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }>
                    {bankStatus === 'approved' ? <><CheckCircle className="w-3 h-3 mr-1" /> Approved</> :
                     bankStatus === 'rejected' ? <><XCircle className="w-3 h-3 mr-1" /> Rejected</> :
                     bankStatus === 'pending' ? <><Clock className="w-3 h-3 mr-1" /> Pending</> :
                     <><AlertCircle className="w-3 h-3 mr-1" /> Not Submitted</>}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {bankDetails ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Business Name</p>
                        <p className="font-medium">{bankDetails.business_account_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Account Number</p>
                        <p className="font-medium">{bankDetails.business_account_number}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Bank Name</p>
                        <p className="font-medium">{bankDetails.bank_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Branch Address</p>
                        <p className="font-medium">{bankDetails.branch_address}</p>
                      </div>
                      {bankDetails.swift_code && (
                        <div>
                          <p className="text-gray-500">SWIFT/Sort Code</p>
                          <p className="font-medium">{bankDetails.swift_code}</p>
                        </div>
                      )}
                      {bankDetails.iban_bic_ifsc && (
                        <div>
                          <p className="text-gray-500">IBAN/BIC/IFSC</p>
                          <p className="font-medium">{bankDetails.iban_bic_ifsc}</p>
                        </div>
                      )}
                      {bankDetails.vat_number && (
                        <div>
                          <p className="text-gray-500">VAT Number</p>
                          <p className="font-medium">{bankDetails.vat_number}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Approval Buttons - Super admin OR employees with agent management permission */}
                    {bankStatus === 'pending' && (user?.is_super_admin || user?.permissions?.can_manage_agents) && (
                      <div className="pt-4 border-t flex gap-2">
                        <Button 
                          onClick={() => handleBankApproval('approve')}
                          disabled={processingBank}
                          className="bg-green-600 hover:bg-green-700 flex-1"
                        >
                          {processingBank ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                          Approve
                        </Button>
                        <Button 
                          onClick={() => setShowRejectDialog(true)}
                          disabled={processingBank}
                          variant="destructive"
                          className="flex-1"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No bank details submitted yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compliance Documents Card - Only visible to super admin OR employees with agent management permission */}
            {(user?.is_super_admin || user?.permissions?.can_manage_agents) && (
            <Card className="border-2 border-blue-200 dark:border-blue-800">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Compliance Documents ({agentDocuments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {agentDocuments.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {agentDocuments.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-700 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium">{doc.document_name}</p>
                            <p className="text-xs text-gray-500">{doc.document_type}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => openAuthenticatedFile(doc.file_url).catch(() => toast.error('Failed to open document'))}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded cursor-pointer"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No documents uploaded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
            
            {/* Partner Agreement Card */}
            {(user?.is_super_admin || user?.permissions?.can_manage_agents) && agentDetails && (
              <AgentAgreement
                agentId={agentId}
                agentName={agentDetails.full_name}
                agentEmail={agentDetails.email}
                agentPhone={agentDetails.phone}
                agentAddress={agentDetails.address}
                agreementData={agreementData}
                onSave={handleSaveAgreement}
                onUpload={async (file) => {
                  // For admin, we'd need a different upload endpoint
                  // For now, this is handled by the agent themselves
                  toast.info('Agents upload their own signed agreements');
                }}
                isAdmin={true}
                readOnly={false}
              />
            )}
          </div>

          {/* Filters Bar */}
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search students, courses, universities..."
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Globe className="w-4 h-4 mr-1 text-gray-500" />
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {filterOptions.countries.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={universityFilter} onValueChange={setUniversityFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="w-4 h-4 mr-1 text-gray-500" />
                    <SelectValue placeholder="University" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Universities</SelectItem>
                    {filterOptions.universities.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={intakeFilter} onValueChange={setIntakeFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Calendar className="w-4 h-4 mr-1 text-gray-500" />
                    <SelectValue placeholder="Intake" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Intakes</SelectItem>
                    {filterOptions.intakes.map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <Filter className="w-4 h-4 mr-1 text-gray-500" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="enrolled">Enrolled</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600">
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Students List */}
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Students ({filteredStudents.length})
              </h3>
              
              {filteredStudents.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No students yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredStudents.map(student => (
                    <div 
                      key={student.id}
                      className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/students/${student.id}`)}
                    >
                      <p className="font-medium text-gray-800 dark:text-white">{student.full_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{student.email}</p>
                      {student.preferred_country && (
                        <p className="text-xs text-gray-400 mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {student.preferred_country}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Applications List */}
            <div className="lg:col-span-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Applications ({filteredApplications.length})
              </h3>
              
              {filteredApplications.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No applications yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {filteredApplications.map(app => (
                    <div 
                      key={app.id}
                      className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer transition-colors border border-transparent hover:border-purple-300"
                      onClick={() => navigate(`/admin/applications/${app.id}`)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-800 dark:text-white truncate">{app.course_name}</p>
                            <Badge className={getStatusBadge(app.status)}>{app.status}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {app.university_name}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {app.student_name}
                            </span>
                            {app.country && (
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {app.country}
                              </span>
                            )}
                            {app.intake_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {app.intake_date}
                              </span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Edit Agent Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-teal-600" />
              Edit Agent Profile
            </DialogTitle>
          </DialogHeader>
          
          {editingAgent && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={editingAgent.full_name || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editingAgent.phone || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={editingAgent.company_name || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, company_name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={editingAgent.country || ''}
                    onChange={(e) => setEditingAgent({ ...editingAgent, country: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={editingAgent.city || ''}
                    onChange={(e) => setEditingAgent({ ...editingAgent, city: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Years Experience</Label>
                  <Input
                    type="number"
                    value={editingAgent.years_experience || 0}
                    onChange={(e) => setEditingAgent({ ...editingAgent, years_experience: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Students Placed</Label>
                  <Input
                    type="number"
                    value={editingAgent.students_placed || 0}
                    onChange={(e) => setEditingAgent({ ...editingAgent, students_placed: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                <input
                  type="checkbox"
                  id="bc-cert"
                  checked={editingAgent.british_council_certified || false}
                  onChange={(e) => setEditingAgent({ ...editingAgent, british_council_certified: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="bc-cert" className="cursor-pointer">British Council Certified</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveAgent} 
                  disabled={savingAgent}
                  className="flex-1 bg-teal-600 text-white hover:bg-teal-700"
                >
                  {savingAgent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bank Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Reject Bank Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Please provide a reason for rejecting the bank details. This will be visible to the agent.
            </p>
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Personal account not accepted. Please provide a business bank account."
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={() => handleBankApproval('reject')}
                disabled={processingBank || !rejectReason.trim()}
                variant="destructive"
                className="flex-1"
              >
                {processingBank ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* POC Assignment Dialog */}
      <Dialog open={showPOCDialog} onOpenChange={setShowPOCDialog}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              Assign Point of Contact
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select an employee to be the Point of Contact (POC) for this agent. POC assignment is mandatory before approval.
            </p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {employees.map((emp) => (
                <div 
                  key={emp.id}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    agentDetails?.agent?.assigned_poc === emp.id
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-amber-300'
                  }`}
                  onClick={() => handleAssignPOC(emp.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">{emp.full_name}</p>
                      <p className="text-sm text-gray-500">{emp.email}</p>
                    </div>
                    {agentDetails?.agent?.assigned_poc === emp.id && (
                      <CheckCircle className="w-5 h-5 text-amber-600" />
                    )}
                  </div>
                </div>
              ))}
              {employees.length === 0 && (
                <p className="text-center text-gray-500 py-4">No employees found</p>
              )}
            </div>
            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => setShowPOCDialog(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentFullPage;
